import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { HandHeart, Plus, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PrayerWall() {
  const { user } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [answeringId, setAnsweringId] = useState(null);
  const [testimony, setTestimony] = useState("");

  const load = () => api.get("/prayers").then((r) => setPrayers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/prayers", { body, anonymous }); setPrayers((p) => [data, ...p]); setBody(""); setShow(false); toast.success("Your request is lifted up."); }
    catch { toast.error("Could not post"); } finally { setBusy(false); }
  };

  const pray = async (id) => {
    try {
      const { data } = await api.post(`/prayers/${id}/pray`);
      setPrayers((p) => p.map((x) => x.id === id ? { ...x, pray_count: data.pray_count, prayed: data.prayed } : x));
    } catch { toast.error("Try again"); }
  };

  const markAnswered = async (id) => {
    if (!testimony.trim()) { toast.error("Share a short praise first"); return; }
    try {
      const { data } = await api.post(`/prayers/${id}/answer`, { testimony });
      setPrayers((p) => p.map((x) => x.id === id ? { ...x, answered: true, testimony: data.testimony } : x));
      setAnsweringId(null); setTestimony("");
      toast.success("Praise God! Testimony shared.");
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Bear One Another's Burdens</span>
              <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight mt-4">Prayer Wall</h1>
              <p className="font-sans text-charcoal/60 mt-4 max-w-xl">Galatians 6:2 — Share what weighs on you. Let the brothers stand with you in prayer.</p>
            </div>
            <button onClick={() => setShow(!show)} data-testid="new-prayer-btn"
              className="inline-flex items-center gap-2 bg-charcoal text-bone px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors">
              <Plus size={14} /> Request Prayer
            </button>
          </div>
        </Reveal>

        {show && (
          <form onSubmit={submit} className="mt-10 border border-charcoal/10 p-8 space-y-6" data-testid="prayer-form">
            <textarea placeholder="What can we pray for?" rows={4} value={body} onChange={(e) => setBody(e.target.value)} required data-testid="prayer-body"
              className="w-full border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive resize-none" />
            <label className="flex items-center gap-3 font-sans text-sm text-charcoal/70 cursor-pointer">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="prayer-anon" className="accent-olive w-4 h-4" />
              Post anonymously
            </label>
            <button disabled={busy} data-testid="prayer-submit" className="bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-olive transition-colors disabled:opacity-50">{busy ? "Posting..." : "Share Request"}</button>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          {prayers.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <div className={`border p-8 h-full flex flex-col justify-between ${p.answered ? "border-gold bg-gold/5" : "border-charcoal/10"}`} data-testid={`prayer-${p.id}`}>
                <div>
                  {p.answered && (
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gold mb-4">
                      <Sparkles size={13} strokeWidth={1.5} /> Answered Prayer
                    </span>
                  )}
                  <p className="font-serif text-2xl leading-snug text-charcoal/90">"{p.body}"</p>
                  {p.answered && p.testimony && (
                    <div className="mt-5 border-l-2 border-gold pl-4">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">Praise Testimony</span>
                      <p className="font-sans text-charcoal/70 mt-1 leading-relaxed">{p.testimony}</p>
                    </div>
                  )}
                </div>
                <div className="mt-8 pt-6 border-t border-charcoal/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs uppercase tracking-widest text-charcoal/50 block">{p.author}</span>
                      <span className="font-mono text-[10px] text-charcoal/40">{p.pray_count} praying</span>
                    </div>
                    {!p.answered && (
                      <button onClick={() => pray(p.id)} data-testid={`pray-btn-${p.id}`}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${p.prayed ? "bg-navy text-bone" : "border border-charcoal/30 hover:bg-charcoal hover:text-bone"}`}>
                        <HandHeart size={15} strokeWidth={1.5} /> {p.prayed ? "Praying" : "I'll Pray"}
                      </button>
                    )}
                  </div>
                  {p.is_author && !p.answered && (
                    answeringId === p.id ? (
                      <div className="mt-5" data-testid={`answer-form-${p.id}`}>
                        <textarea value={testimony} onChange={(e) => setTestimony(e.target.value)} rows={2}
                          placeholder="Share how God answered..." data-testid="answer-testimony"
                          className="w-full border border-charcoal/20 bg-transparent p-3 font-sans text-sm focus:outline-none focus:border-gold resize-none" />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => markAnswered(p.id)} data-testid={`answer-submit-${p.id}`}
                            className="inline-flex items-center gap-2 bg-gold text-charcoal px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-gold/80 transition-colors">
                            <Check size={14} /> Share Praise
                          </button>
                          <button onClick={() => { setAnsweringId(null); setTestimony(""); }} className="font-mono text-xs uppercase tracking-widest text-charcoal/50 px-2">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAnsweringId(p.id); setTestimony(""); }} data-testid={`mark-answered-${p.id}`}
                        className="mt-4 font-mono text-xs uppercase tracking-widest text-gold hover:underline flex items-center gap-2">
                        <Check size={14} /> Mark as Answered
                      </button>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          {prayers.length === 0 && <p className="font-sans text-charcoal/50 col-span-2">No requests yet. Be the first to share.</p>}
        </div>
      </div>
    </div>
  );
}
